import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import styles from './DLNAPage.module.css';

/**
 * 云E投屏 · 产品官网
 * UI 风格：暗色渐变（沿用原官网 HTML 设计）
 * 功能整合：直接下载 APK + 扫描下载（二维码）+ 应用商店渠道
 *
 * 图片资源位于 public/dlna/ 目录下，通过 /dlna/xxx 访问
 * 应用场景图片位于 public/dlna/yycj/ 目录下
 */

/* ============ 滚动进入视口动画 Hook ============ */
const useReveal = () => {
  const pageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;

    const targets = root.querySelectorAll<HTMLElement>('[data-reveal]');
    if (!targets.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add(styles.isIn));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.isIn);
            io.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.12,
      }
    );

    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return pageRef;
};

const DLNAPage: React.FC = () => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [currentScreenshot, setCurrentScreenshot] = useState(0);

  const pageRef = useReveal();

  const appName = '云E投屏';
  const apkUrl =
    'https://iaya.cn/app/dlna/lts/cscdlna_official_v1.0.2.2533_20261026_111339_ye_lts.apk';

  const screenshots = [
    { id: 1, label: '添加视频文件', src: '/dlna/添加视频文件.jpg' },
    { id: 2, label: '视频投屏播放', src: '/dlna/视频投屏.jpg' },
    { id: 3, label: '选择投屏设备', src: '/dlna/在下拉列表选择投屏设备.jpg' },
    { id: 4, label: '选择镜像投屏', src: '/dlna/选择镜像投屏.jpg' },
    { id: 5, label: '镜像状态栏提示', src: '/dlna/镜像投屏手机端显示，状态栏.jpg' },
    { id: 6, label: '跟读模式 · 阅读', src: '/dlna/跟读模式.jpg' },
    { id: 7, label: '跟读模式 · 学习', src: '/dlna/跟读模式之学习模式.jpg' },
    { id: 8, label: '跟读片段列表', src: '/dlna/跟读模式2.jpg' },
    { id: 9, label: '通知栏暂停/停止', src: '/dlna/镜像投屏状态栏停止.jpg' },
    { id: 10, label: '投屏中 · 本地界面', src: '/dlna/选择投屏设备.jpg' },
  ];

  const scenes = [
    { id: 1, title: '学习辅助', src: '/dlna/yycj/xuexi_fuzhu.png' },
    { id: 2, title: '办公会议投屏', src: '/dlna/yycj/bangong_huiyi.png' },
    { id: 3, title: '文档投屏', src: '/dlna/yycj/wendang_touping.png' },
    { id: 4, title: '大型会议多电视投屏', src: '/dlna/yycj/daxing_huiyi.png' },
    { id: 5, title: '在线网课', src: '/dlna/yycj/zaixian_wangke.png' },
    { id: 6, title: '跨设备协作', src: '/dlna/yycj/kuashebei_xiezuo.png' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentScreenshot((prev) => (prev + 1) % screenshots.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [screenshots.length]);

  useEffect(() => {
    const generateQR = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(apkUrl, {
          width: 260,
          margin: 2,
          color: { dark: '#1e293b', light: '#ffffff' },
        });
        setQrCodeUrl(dataUrl);
      } catch (error) {
        console.error('生成二维码失败:', error);
      }
    };
    generateQR();
  }, [apkUrl]);

  const handleDownload = () => {
    window.open(apkUrl, '_blank');
  };

  return (
    <div className={styles.cloudPage} ref={pageRef}>
      {/* ============= 导航 ============= */}
      <nav className={styles.cloudNav}>
        <div className={`${styles.cloudWrap} ${styles.cloudNavIn}`}>
          <a className={styles.cloudLogo} href="#">
            <span className={styles.cloudLogoDot}>📺</span>
            云E投屏
          </a>
          <div className={styles.cloudNavLinks}>
            <a href="#features">功能特性</a>
            <a href="#follow">跟读模式</a>
            <a href="#mirror">镜像投屏</a>
            <a href="#gallery">应用截图</a>
            <a href="#scenes">应用场景</a>
          </div>
          <a
            className={`${styles.cloudBtn} ${styles.cloudBtnP}`}
            href="#download"
          >
            立即下载
          </a>
        </div>
      </nav>

      {/* ============= Hero ============= */}
      <header className={styles.cloudHero}>
        <div className={`${styles.cloudWrap} ${styles.cloudHeroIn}`}>
          <div data-reveal>
            <h1 className={styles.cloudProductName}>
              <img
                className={styles.cloudProductLogo}
                src="/dlna/v3.png"
                alt="云E投屏 Logo"
              />
              <span className={styles.cloudProductText}>云E投屏</span>
            </h1>
            <span className={styles.cloudTag}>
              ✨ 投屏 · 镜像 · 跟读学习，三合一
            </span>
            <h2 className={styles.cloudMainTitle}>
              把精彩内容
              <br />
              <span className={styles.cloudGrad}>一键投上大屏幕</span>
            </h2>
            <p className={styles.cloudLead}>
              本地视频轻松投电视，手机屏幕实时镜像，更内置智能跟读学习模式——孩子的《三字经》学习、全家的观影时光，一个 App 全搞定。
            </p>
            <div className={styles.cloudHeroCta}>
              <a
                className={`${styles.cloudBtn} ${styles.cloudBtnP}`}
                href="#download"
              >
                📲 Android / HarmonyOS 1.0.2.2533 下载（LTS）
              </a>
              <a
                className={`${styles.cloudBtn} ${styles.cloudBtnG}`}
                href="#features"
              >
                了解功能 ↓
              </a>
              <a
                className={`${styles.cloudBtn} ${styles.cloudBtnG}`}
                href="/products/software/video"
              >
                ▶ 操作视频教程
              </a>
            </div>
            <div className={styles.cloudStats}>
              <div className={styles.cloudStat}>
                <b>3 种</b>
                <span>投屏/播放模式</span>
              </div>
              <div className={styles.cloudStat}>
                <b>34 段</b>
                <span>智能跟读切片</span>
              </div>
              <div className={styles.cloudStat}>
                <b>50ms</b>
                <span>片段微调精度</span>
              </div>
            </div>
          </div>

          <div className={styles.cloudHeroR} data-reveal data-reveal-delay="2">
            <div className={styles.cloudPhone}>
              <img src="/dlna/视频投屏.jpg" alt="视频投屏" />
            </div>
            <div className={`${styles.cloudFloatCard} ${styles.cloudFc1}`}>
              <i>📶</i>已连接 · 云E投屏电脑终端
            </div>
            <div className={`${styles.cloudFloatCard} ${styles.cloudFc2}`}>
              <i>🎵</i>三字经3.mp4 投屏播放中
            </div>
          </div>
        </div>
      </header>

      {/* ============= 功能特性 ============= */}
      <section id="features" className={styles.cloudSection}>
        <div className={styles.cloudWrap}>
          <div className={styles.cloudSecHead} data-reveal>
            <div className={styles.cloudKicker}>FEATURES</div>
            <h2>为家庭大屏体验而生</h2>
            <p>
              从本地播放到电视投屏，从屏幕镜像到跟读学习，每一项功能都精雕细琢。
            </p>
          </div>
          <div className={styles.cloudGrid6}>
            <div className={styles.cloudCard} data-reveal data-reveal-delay="1">
              <div className={styles.cloudIc}>📺</div>
              <h3>视频一键投屏</h3>
              <p>
                自动搜索局域网内 DLNA 电视与电脑终端，选择设备即刻推送，大屏观看更尽兴。
              </p>
            </div>
            <div className={styles.cloudCard} data-reveal data-reveal-delay="2">
              <div className={styles.cloudIc}>🪞</div>
              <h3>镜像投屏</h3>
              <p>
                RTSP 实时投射手机屏幕，音画同步含音频，通知栏常驻快捷控制，随时暂停或停止。
              </p>
            </div>
            <div className={styles.cloudCard} data-reveal data-reveal-delay="3">
              <div className={styles.cloudIc}>📖</div>
              <h3>智能跟读模式</h3>
              <p>
                自动分析音频切分跟读片段，阅读 / 学习双模式一键切换，让经典诵读更高效。
              </p>
            </div>
            <div className={styles.cloudCard} data-reveal data-reveal-delay="4">
              <div className={styles.cloudIc}>✂️</div>
              <h3>片段精调</h3>
              <p>
                单击播放片段，[-]/[+] 以 50ms 步长微调起止位置并联动相邻片段，长按即可拆分合并。
              </p>
            </div>
            <div className={styles.cloudCard} data-reveal data-reveal-delay="5">
              <div className={styles.cloudIc}>🗂️</div>
              <h3>播放列表管理</h3>
              <p>
                本地播放（不投屏）与投屏双模式，多文件列表、拖拽排序、进度记忆，一切井井有条。
              </p>
            </div>
            <div className={styles.cloudCard} data-reveal data-reveal-delay="6">
              <div className={styles.cloudIc}>🔁</div>
              <h3>导入导出</h3>
              <p>
                跟读片段数据支持导出与导入，精心调整的学习进度，换设备也能无缝接续。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============= 跟读模式 ============= */}
      <section
        id="follow"
        className={`${styles.cloudSection} ${styles.cloudSectionAlt}`}
      >
        <div className={`${styles.cloudWrap} ${styles.cloudSplit}`}>
          <div className={styles.cloudPhones} data-reveal data-reveal-delay="1">
            <div className={`${styles.cloudPhone} ${styles.cloudPhoneSm}`}>
              <img src="/dlna/跟读模式.jpg" alt="跟读模式-阅读" />
            </div>
            <div className={`${styles.cloudPhone} ${styles.cloudPhoneSm}`}>
              <img src="/dlna/跟读模式之学习模式.jpg" alt="跟读模式-学习" />
            </div>
          </div>
          <div className={styles.cloudTxt} data-reveal data-reveal-delay="2">
            <div className={styles.cloudKicker}>FOLLOW-READING</div>
            <h2>
              跟读模式：把视频变成
              <br />
              孩子的随身老师
            </h2>
            <p className={styles.cloudDim}>
              一段《三字经》视频，自动分析切分出 34 个跟读片段。每个片段单击即播、逐句跟读，学习节奏由你掌控。
            </p>
            <div className={styles.cloudChk}>
              <div className={styles.cloudChkIc}>📕</div>
              <div>
                <h4>阅读 / 学习双模式</h4>
                <p>阅读模式整段聆听，学习模式逐句跟读，一键切换。</p>
              </div>
            </div>
            <div className={styles.cloudChk}>
              <div className={styles.cloudChkIc}>⏱️</div>
              <div>
                <h4>毫秒级精调</h4>
                <p>50ms 步长微调起止点，自动联动相邻片段，贴合每句节奏。</p>
              </div>
            </div>
            <div className={styles.cloudChk}>
              <div className={styles.cloudChkIc}>🧩</div>
              <div>
                <h4>拆分与合并</h4>
                <p>长按片段即可拆分过长的句子、合并过短的断句。</p>
              </div>
            </div>
            <div className={styles.cloudChk}>
              <div className={styles.cloudChkIc}>💾</div>
              <div>
                <h4>重新分析 · 导入导出</h4>
                <p>对结果不满意可重新分析；调整成果随时备份、随时恢复。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============= 镜像投屏 ============= */}
      <section
        id="mirror"
        className={`${styles.cloudSection} ${styles.cloudMirrorBox}`}
      >
        <div
          className={`${styles.cloudWrap} ${styles.cloudSplit} ${styles.cloudSplitRev}`}
        >
          <div className={styles.cloudTxt} data-reveal data-reveal-delay="2">
            <div className={styles.cloudKicker}>SCREEN MIRRORING</div>
            <h2>
              镜像投屏：手机上的一切
              <br />
              都实时呈现在大屏
            </h2>
            <div className={styles.cloudSteps}>
              <div className={styles.cloudStep}>
                <div>
                  <h4>打开扩展功能，选择镜像投屏</h4>
                  <p>底部菜单轻点「镜像投屏」，将手机屏幕实时投射到大屏。</p>
                </div>
              </div>
              <div className={styles.cloudStep}>
                <div>
                  <h4>选择投屏设备</h4>
                  <p>
                    自动发现局域网设备，显示名称与 IP（如 192.168.2.19），点选即连。
                  </p>
                </div>
              </div>
              <div className={styles.cloudStep}>
                <div>
                  <h4>开始 RTSP 投屏（含音频）</h4>
                  <p>电视实时同步显示手机画面，音画一体，延迟低、画质清晰。</p>
                </div>
              </div>
              <div className={styles.cloudStep}>
                <div>
                  <h4>通知栏随时掌控</h4>
                  <p>
                    下拉通知栏即可「暂停 / 停止」投屏，无需回到应用，方便省心。
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-reveal data-reveal-delay="1">
            <div className={styles.cloudTvFrame}>
              <img src="/dlna/镜像投屏电视上显示.png" alt="电视镜像显示" />
            </div>
            <p className={styles.cloudTvCaption}>▲ 电视端实时镜像效果</p>
          </div>
        </div>
      </section>

      {/* ============= 应用截图 ============= */}
      <section
        id="gallery"
        className={`${styles.cloudSection} ${styles.cloudSectionAlt}`}
      >
        <div className={styles.cloudWrap}>
          <div className={styles.cloudSecHead} data-reveal>
            <div className={styles.cloudKicker}>SCREENSHOTS</div>
            <h2>应用截图一览</h2>
            <p>每一个界面，都为清晰与易用而设计。</p>
          </div>
          <div className={styles.cloudGal}>
            {screenshots.map((s, index) => (
              <figure
                key={s.id}
                data-reveal
                data-reveal-delay={String((index % 5) + 1)}
              >
                <div
                  className={`${styles.cloudGalPhone} ${
                    currentScreenshot === index
                      ? styles.cloudGalPhoneActive
                      : ''
                  }`}
                  onClick={() => setCurrentScreenshot(index)}
                >
                  <img src={s.src} alt={s.label} />
                </div>
                <figcaption>{s.label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============= 应用场景 ============= */}
      <section id="scenes" className={styles.cloudSceneSection}>
        {/* 上半：标题区（叠 hero_bg.png 背景图） */}
        <div className={styles.cloudSceneHero}>
          <div className={styles.cloudSceneBg} aria-hidden="true">
            <img
              className={styles.cloudSceneBgImg}
              src="/dlna/yycj/hero_bg.png"
              alt=""
            />
          </div>

          <div className={`${styles.cloudWrap} ${styles.cloudSceneHeroInner}`}>
            <div className={styles.cloudSceneHead} data-reveal>
              <h2 className={styles.cloudSceneTitle}>&nbsp;</h2>
              <p className={styles.cloudSceneSubtitle}>
                
              </p>
              <p className={styles.cloudSceneLead}>
                
              </p>
            </div>
          </div>
        </div>

        {/* 下半：6 张场景图（图片本身已含文字，只铺满显示） */}
        <div className={styles.cloudSceneCards}>
          <div className={styles.cloudWrap}>
            <div className={styles.cloudSceneGrid}>
              {scenes.map((sc, i) => (
                <div
                  key={sc.id}
                  className={styles.cloudSceneCard}
                  data-reveal
                  data-reveal-delay={String((i % 3) + 1)}
                >
                  <img
                    className={styles.cloudSceneCardImg}
                    src={sc.src}
                    alt={sc.title}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============= 下载 ============= */}
      <section id="download" className={styles.cloudDownloadSection}>
        <div className={`${styles.cloudWrap} ${styles.cloudDlInner}`}>
          <div
            className={styles.cloudDlHeader}
            data-reveal
            data-reveal-delay="1"
          >
            <img
              className={styles.cloudDlIcon}
              src="/dlna/v3.png"
              alt="云E投屏图标"
            />
            <div className={styles.cloudDlHeaderText}>
              <h2 className={styles.cloudDlTitle}>云E投屏</h2>
              <p className={styles.cloudDlDesc}>
                无线投屏协作工具，支持手机、平板等设备快速投屏，实现多屏互动，让演示和分享更加便捷高效。
              </p>
              <div className={styles.cloudDlStoreRow}>
                <span className={styles.cloudDlStoreHint}>
                  🔍 应用商店中搜索「<b>云E投屏</b>」即可下载
                </span>
                <a className={styles.cloudDlStoreBtn} >
                   <img
      src="/images/platforms/huawei.png"
      alt="华为应用商店"
      className={styles.cloudDlStoreIcon}
    />
                  华为应用商店
                </a>
                <a className={styles.cloudDlStoreBtn} >
                   <img
      src="/images/platforms/vivo.png"
      alt="VIVO应用商店"
      className={styles.cloudDlStoreIcon}
    />
                  VIVO应用商店
                </a>
              </div>
            </div>
          </div>

          <div className={styles.cloudDownloadDual}>
            <div
              className={styles.cloudDlBlock}
              data-reveal
              data-reveal-delay="2"
            >
              <div className={styles.cloudDlBlockTitle}>
                <i>⚡</i> 直接下载
              </div>
              <button
                className={styles.cloudBtnDirect}
                onClick={handleDownload}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                直接下载 APK
              </button>
              <p className={styles.cloudDlHint}>适用于 Android 8.0 及以上</p>
            </div>

            <div
              className={styles.cloudDlBlock}
              data-reveal
              data-reveal-delay="3"
            >
              <div className={styles.cloudDlBlockTitle}>
                <i>📷</i> 扫描下载
              </div>
              <div className={styles.cloudQrWrap}>
                {qrCodeUrl ? (
                  <img src={qrCodeUrl} alt="下载二维码" />
                ) : (
                  <div className={styles.cloudQrPlaceholder}>
                    <span>🔄</span>
                    <p>生成二维码...</p>
                  </div>
                )}
              </div>
              <p className={styles.cloudQrTip}>
                手机扫码下载 <b>云E投屏</b>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DLNAPage;